'use client'

import 'animate.css/animate.min.css'

import { AnimationOnScroll } from 'react-animation-on-scroll'
import Image from 'next/image'

import cost from './icons/cost.svg'
import raft from './icons/raft.svg'

import ageStructureData from './data/2020/ageStructure.json'
import turnoverData from './data/2020/turnover.json'
import emplooyeesData from './data/2020/employees.json'
import projectsData from './data/2020/projects.json'
import onlineData from './data/2020/online.json'

import SmallNumberBox from './SmallNumberBox'
import AgeStructureBox from './AgeStructureBox'
import RevenuesBox from './RevenuesBox'
import EmployeesBox from './EmployeesBox'
import ChartBox from './ChartBox'
import { useLocale } from 'next-intl'
import type { Locale } from '@/i18n'

const darkBlue = '#266782'

// Comment in another animation to play around.
// Any animation from https://daneden.github.io/animate.css/ can be used
//
const animationProps = {
  animateIn: 'fadeIn',
  duration: Math.random() * 0.75 + 1,
  delay: Math.random() * 300,
  animateOnce: true,
}

const ChatBox = () => {
  const locale = useLocale() as Locale

  return (
    <SmallNumberBox
      animationProps={animationProps}
      title={onlineData['title'][locale]}
      titleColor={'white'}
      backgroundColor={darkBlue}
      borderColor={darkBlue}
      bigTitle={true}
    >
      <div className="relative my-4">
        <svg
          className="mx-auto block"
          style={{
            display: 'block',
            color: 'white',
            width: '80%',
            fill: 'white',
          }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 43 38.9"
        >
          <path d="M43,22.52V27.2a3.23,3.23,0,0,1-2.43,3c-.43,3.35-3.68,6-7.84,6.54-.49,1.24-2.11,2.13-4.08,2.13-2.38,0-4.25-1.3-4.25-2.95s1.87-3,4.25-3a4.38,4.38,0,0,1,4,1.95c3.07-.42,5.46-2.32,5.89-4.69A3.22,3.22,0,0,1,36,27.2V22.52a3.26,3.26,0,0,1,2.5-3.05v-.63c0-9.86-7.15-17-17-17s-17,7.16-17,17v.63A3.26,3.26,0,0,1,7,22.52V27.2a3.36,3.36,0,0,1-3.5,3.18A3.36,3.36,0,0,1,0,27.2V22.52a3.26,3.26,0,0,1,2.5-3.05v-.63C2.5,7.92,10.49,0,21.5,0s19,7.92,19,18.84v.63A3.26,3.26,0,0,1,43,22.52Z" />
        </svg>

        <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center text-8xl text-white">
          {onlineData['value']}
        </div>
      </div>
    </SmallNumberBox>
  )
}

const Adventures = () => {
  const locale = useLocale() as Locale

  return (
    <AnimationOnScroll
      {...animationProps}
      className="flex h-full w-full flex-col items-center border border-solid text-center text-start uppercase"
      style={{
        scrollSnapAlign: 'center',
        borderColor: darkBlue,
      }}
    >
      <div className="relative w-full" style={{ minHeight: 250 }}>
        <Image
          src={cost}
          alt="cost"
          className="absolute right-0 top-0 origin-top-right scale-100 sm:scale-150"
        />
        <Image src={raft} alt="raft" className="absolute bottom-0 left-0" />
        <div className="z-1 text-darkBlue relative my-32">
          <div className="text-9xl font-medium">{projectsData['total']}</div>
          <div className="my-2 text-3xl font-medium sm:my-4 sm:text-4xl">
            {projectsData['title'][locale]}
          </div>
        </div>
      </div>
    </AnimationOnScroll>
  )
}

const NumbersAndFacts2020 = () => {
  const locale = useLocale() as Locale

  return (
    <>
      <div className="mb-4 mt-16 flex w-full flex-grow flex-row flex-wrap justify-center sm:space-x-4">
        <div className="mb-4 flex flex-1 flex-col sm:mb-0">
          <AgeStructureBox
            ageStructureData={ageStructureData}
            animationProps={animationProps}
          />
        </div>

        <div className="grow-1 align-center flex w-full max-w-full flex-col justify-center space-y-4 sm:w-auto sm:flex-initial">
          <RevenuesBox
            animationProps={animationProps}
            title={emplooyeesData['growthRevenue'][locale]}
            year={emplooyeesData['yearSpan'][locale]}
            revenue={emplooyeesData['revenue']}
            revenueLabel={emplooyeesData['revenueLabel'][locale]}
          />
          <EmployeesBox
            animationProps={animationProps}
            title={emplooyeesData['title'][locale]}
          >
            {emplooyeesData['total']}
          </EmployeesBox>
        </div>
      </div>

      <div className="align-center flex w-full max-w-full flex-wrap justify-center md:flex-nowrap md:space-x-4">
        <div className="md:max-w-1/2 mb-4 flex max-w-full flex-auto flex-col md:mb-0">
          <ChatBox />
        </div>
        <div className="flex flex-auto flex-grow flex-col" style={{}}>
          <ChartBox
            animationProps={animationProps}
            title={turnoverData['title'][locale]}
            barValues={turnoverData['values']}
            tickValues={[0, 5, 10, 15, 20, 25, 30, 35, 40]}
            hightlightedBar="2020"
          />
        </div>
      </div>

      <div className="mt-4">
        <Adventures />
      </div>
    </>
  )
}
export default NumbersAndFacts2020
